import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { Wrench, AlertTriangle, RefreshCw } from 'lucide-react';

const MaintenanceCheck = ({ children }) => {
  const { isGlobalAdmin, loading: authLoading } = useSimpleAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [allowAdminAccess, setAllowAdminAccess] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Suscribirse a cambios en la configuración del sistema
    const unsubscribe = onSnapshot(
      doc(db, 'systemConfig', 'maintenance'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setMaintenanceMode(data.maintenanceMode || false);
          setMaintenanceMessage(data.maintenanceMessage || 'El sistema está en mantenimiento. Por favor, vuelve más tarde.');
          setAllowAdminAccess(data.allowAdminAccess !== false);
        } else {
          setMaintenanceMode(false);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error verificando modo mantenimiento:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Mientras carga, no mostrar nada (evitar flash)
  if (loading || authLoading) {
    return children;
  }

  // Si está en mantenimiento
  if (maintenanceMode) {
    // Permitir acceso a globalAdmin si está configurado
    if (allowAdminAccess && isGlobalAdmin) {
      return (
        <>
          {/* Banner de advertencia para admin */}
          <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-yellow-900 px-4 py-2 text-center text-sm font-medium">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            MODO MANTENIMIENTO ACTIVO - Solo visible para Super Admin
          </div>
          <div className="pt-10">
            {children}
          </div>
        </>
      );
    }

    // Mostrar pantalla de mantenimiento para usuarios normales
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          {/* Icono animado */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center">
              <Wrench className="w-12 h-12 text-amber-500 animate-pulse" />
            </div>
          </div>

          {/* Título */}
          <h1 className="text-3xl font-bold text-white mb-4">
            Sitio en Mantenimiento
          </h1>

          {/* Mensaje */}
          <p className="text-gray-400 mb-8 leading-relaxed">
            {maintenanceMessage}
          </p>

          {/* Spinner */}
          <div className="flex items-center justify-center gap-2 text-amber-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="text-sm">Trabajando en ello...</span>
          </div>

          {/* Información adicional */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <p className="text-gray-500 text-sm">
              Si tienes urgencia, contacta con soporte
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si no está en mantenimiento, mostrar contenido normal
  return children;
};

export default MaintenanceCheck;

